import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, ListGroup } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { TeamConfirmModal } from '../team/TeamConfirmModal';
import { leaveErrorMessage } from '../team/leaveErrorMessage';
import { useWorkspaces, WorkspaceSummary } from './useWorkspaces';
import { useSwitchWorkspace } from './useSwitchWorkspace';
import { useLeaveWorkspace } from './useLeaveWorkspace';
import { workspaceMessages } from './workspaceMessages';
import { WorkspaceSwitchError } from './WorkspaceSwitchError';
import { YourWorkspacesRow } from './YourWorkspacesRow';
import { LeaveWorkspaceModalBody } from './LeaveWorkspaceModalBody';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

/**
 * design: workspaces d1 / e1 / e3 "Your workspaces", under the Team tab of an owner and of a
 * member. Nothing with only the own workspace, except for an old-model member (D8, e3): their only
 * row is a MEMBER row, and the row tells them why they cannot leave it.
 * Leave opens the e2 confirm. The confirm stays open while the call runs; an error shows in this card.
 * A failed `workspaces.list` shows ErrorView with a retry.
 */
const YourWorkspacesCardComponent: React.FC = () => {
  const intl = useIntl();
  const list = useWorkspaces();
  const switcher = useSwitchWorkspace();
  const leaving = useLeaveWorkspace();
  const [confirming, setConfirming] = useState<WorkspaceSummary | null>(null);
  const { rows, switchTo, pendingLicenseId, failure } = switcher;
  const homeName = useMemo(() => rows.find((workspace) => workspace.isHome)?.name ?? null, [rows]);

  const openSwitch = useCallback((workspace: WorkspaceSummary) => switchTo(workspace.licenseId), [switchTo]);
  const cancel = useCallback(() => setConfirming(null), []);
  const { leave } = leaving;
  const confirm = useCallback(() => {
    if (confirming !== null) {
      leave(confirming, () => setConfirming(null));
    }
  }, [confirming, leave]);

  const title = intl.formatMessage({ id: 'app.sml.workspaces.list.title', defaultMessage: 'Your workspaces' });
  if (list.error !== null) {
    return (
      <Panel as="section" className="mt-4">
        <PanelHead title={title} />
        <PanelBody>
          <ErrorView error={list.error} retry={() => list.refetch().catch(console.error)} />
        </PanelBody>
      </Panel>
    );
  }
  const isOnlyOldModelHome = rows.length === 1 && rows[0]?.role === 'MEMBER';
  if (rows.length < 2 && !isOnlyOldModelHome) {
    return null;
  }
  const isBusy = pendingLicenseId !== null || leaving.pendingLicenseId !== null;
  return (
    <Panel as="section" className="mt-4">
      <PanelHead title={title} right={intl.formatMessage(workspaceMessages.count, { count: rows.length })} />
      <PanelBody>
        <ListGroup flush tag="ul" className="mb-0">
          {rows.map((workspace) => (
            <YourWorkspacesRow
              key={workspace.licenseId}
              workspace={workspace}
              isBusy={isBusy}
              onSwitch={openSwitch}
              onLeave={setConfirming}
            />
          ))}
        </ListGroup>
        {failure !== null && (
          <WorkspaceSwitchError
            failure={failure}
            workspaceName={failure.workspaceName}
            onRetry={() => switchTo(failure.licenseId)}
          />
        )}
        {leaving.error !== null && leaving.error !== undefined && (
          <Alert color="danger" className="mt-3 mb-0">
            {leaveErrorMessage(intl, leaving.error, leaving.errorWorkspaceName)}
          </Alert>
        )}
      </PanelBody>
      <TeamConfirmModal
        isOpen={confirming !== null}
        title={intl.formatMessage(workspaceMessages.leaveTitle, { workspace: confirming?.name ?? '' })}
        confirmLabel={intl.formatMessage(workspaceMessages.leaveConfirm)}
        isPending={leaving.pendingLicenseId !== null}
        onConfirm={confirm}
        onCancel={cancel}
      >
        <LeaveWorkspaceModalBody
          workspace={confirming}
          homeName={homeName}
          fallbackBody={intl.formatMessage(workspaceMessages.leaveBody)}
        />
      </TeamConfirmModal>
    </Panel>
  );
};

export const YourWorkspacesCard = TypedMemo(YourWorkspacesCardComponent);
